/**
 * The panel in front, kept with the view each browser history entry shows, so Back and Forward
 * bring back the view and not only its panels: a phone, or a stage too narrow for the row, shows
 * that one panel alone. Kept in session storage, which lives as long as the tab's history does.
 */

import { isPanelId } from '@/app/panelIds';
import { sessionStore } from '@/lib/webStorage';
import type { PanelId } from '@/types/panels';

const STORAGE_KEY = 'vms.panels.focusByView';

// Views remembered, most recent last: more than a Back walk reaches.
const VIEW_FOCUS_LIMIT = 50;

/** A view and the panel in front of it; null when nothing is. */
type ViewFocus = [view: string, focusedId: PanelId | null];

/** The view an entry shows: its URL without the playhead, which every seek rewrites. */
function getView(search: string): string {
    const params = new URLSearchParams(search);
    params.delete('t');
    return params.toString();
}

function isViewFocus(value: unknown): value is ViewFocus {
    return Array.isArray(value) && value.length === 2 && typeof value[0] === 'string' &&
        (value[1] === null || isPanelId(value[1]));
}

function readViewFocus(): ViewFocus[] {
    const storedValue = sessionStore.readJson(STORAGE_KEY);
    return Array.isArray(storedValue) ? storedValue.filter(isViewFocus) : [];
}

/** The saved entry of the view that `search` shows. */
export function findSavedFocus(search: string): ViewFocus | undefined {
    const view = getView(search);
    return readViewFocus().find(([savedView]) => savedView === view);
}

export function writeSavedFocus(search: string, focusedId: PanelId | null): void {
    const view = getView(search);
    const others = readViewFocus().filter(([savedView]) => savedView !== view);
    const nextViewFocuses: ViewFocus[] = [...others, [view, focusedId]];
    sessionStore.writeJson(STORAGE_KEY, nextViewFocuses.slice(-VIEW_FOCUS_LIMIT));
}
